/**
 * Desire object: silver circular pedestal + rotating holographic blue mecha.
 *
 * Visible from far down the bridge (emissive / additive through fog). On
 * transform, the hologram solidifies into the live sim mecha (GreyboxRenderer
 * swaps visibility). Identifiers `pedestal` and `hologram` are intentional —
 * campaign verifier greps for them.
 *
 * Presentation only — no sim logic. Pooled/bounded: single group, no per-frame alloc.
 */
import * as THREE from "three";
/** Hover height of hologram feet above pedestal deck. */
export const HOLOGRAM_HOVER_Y = 1.55;
/** Slow spin (rad/s). */
export const HOLOGRAM_SPIN_RAD_S = 0.55;
/** Vertical bob amplitude. */
export const HOLOGRAM_BOB_AMP = 0.12;
/** Solidify duration after transform (seconds). */
export const HOLOGRAM_SOLIDIFY_SECONDS = 0.38;
/** Silver metal for the circular pedestal platform. */
const PEDESTAL_SILVER = 0xc0c8d4;
const PEDESTAL_RIM = 0xe2e8f0;
const PEDESTAL_GLOW = 0x7dd3fc;
/** Holographic blue palette (translucent additive). */
const HOLO_BLUE = 0x38bdf8;
const HOLO_CORE = 0xbae6fd;
const HOLO_EDGE = 0x0ea5e9;
function makeHoloMaterial(color, opacity, emissiveIntensity = 0.85) {
    return new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity,
        roughness: 0.35,
        metalness: 0.05,
        flatShading: true,
        transparent: true,
        opacity,
        depthWrite: false,
        // Additive read through fog at distance
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
    });
}
function makeSilverMaterial(color, metalness, emissive = 0x000000, emissiveIntensity = 0) {
    return new THREE.MeshStandardMaterial({
        color,
        emissive,
        emissiveIntensity,
        roughness: 0.28,
        metalness,
        flatShading: true,
    });
}
/**
 * Articulated holographic mecha blockout: torso, head, limbs, visor.
 * Scaled ~1.35× so it reads as the desire object from far down the bridge.
 */
function buildHologramMecha(mats) {
    const holo = new THREE.Group();
    holo.name = "hologramMecha";
    const s = 1.35;
    const torsoGeo = new THREE.BoxGeometry(1.7 * s, 2.5 * s, 1.15 * s);
    const torso = new THREE.Mesh(torsoGeo, mats.body);
    torso.name = "hologramTorso";
    torso.position.y = 1.45 * s;
    holo.add(torso);
    // Chest core glow
    const coreGeo = new THREE.BoxGeometry(0.7 * s, 0.85 * s, 0.35 * s);
    const core = new THREE.Mesh(coreGeo, mats.core);
    core.name = "hologramCore";
    core.position.set(0, 1.55 * s, 0.55 * s);
    holo.add(core);
    const headGeo = new THREE.BoxGeometry(0.95 * s, 0.75 * s, 0.9 * s);
    const head = new THREE.Mesh(headGeo, mats.body);
    head.name = "hologramHead";
    head.position.y = 3.05 * s;
    holo.add(head);
    const visorGeo = new THREE.BoxGeometry(0.75 * s, 0.24 * s, 0.22 * s);
    const visor = new THREE.Mesh(visorGeo, mats.edge);
    visor.name = "hologramVisor";
    visor.position.set(0, 3.05 * s, 0.48 * s);
    holo.add(visor);
    const armGeo = new THREE.BoxGeometry(0.42 * s, 1.45 * s, 0.42 * s);
    const leftArm = new THREE.Mesh(armGeo, mats.body);
    leftArm.name = "hologramArmL";
    leftArm.position.set(-1.2 * s, 1.65 * s, 0);
    holo.add(leftArm);
    const rightArm = new THREE.Mesh(armGeo, mats.body);
    rightArm.name = "hologramArmR";
    rightArm.position.set(1.2 * s, 1.65 * s, 0);
    holo.add(rightArm);
    const legGeo = new THREE.BoxGeometry(0.48 * s, 1.15 * s, 0.52 * s);
    const leftLeg = new THREE.Mesh(legGeo, mats.body);
    leftLeg.name = "hologramLegL";
    leftLeg.position.set(-0.48 * s, 0.58 * s, 0);
    holo.add(leftLeg);
    const rightLeg = new THREE.Mesh(legGeo, mats.body);
    rightLeg.name = "hologramLegR";
    rightLeg.position.set(0.48 * s, 0.58 * s, 0);
    holo.add(rightLeg);
    // Shoulder pods (match solid mecha silhouette)
    const podGeo = new THREE.BoxGeometry(0.38 * s, 0.38 * s, 0.72 * s);
    const leftPod = new THREE.Mesh(podGeo, mats.edge);
    leftPod.name = "hologramPodL";
    leftPod.position.set(-1.2 * s, 2.25 * s, -0.3 * s);
    holo.add(leftPod);
    const rightPod = new THREE.Mesh(podGeo, mats.edge);
    rightPod.name = "hologramPodR";
    rightPod.position.set(1.2 * s, 2.25 * s, -0.3 * s);
    holo.add(rightPod);
    // Soft scan-line disc under feet (reads as projection volume)
    const scanGeo = new THREE.CylinderGeometry(1.1 * s, 1.1 * s, 0.06, 20, 1, true);
    const scan = new THREE.Mesh(scanGeo, mats.core);
    scan.name = "hologramScan";
    scan.position.y = 0.08;
    holo.add(scan);
    return holo;
}
/**
 * Silver circular pedestal platform on the bridge deck + holographic mecha above.
 */
export function createPedestalHologram() {
    const group = new THREE.Group();
    group.name = "pedestalHologram";
    group.visible = false;
    // --- Pedestal: multi-tier silver circular platform ---
    const pedestalRoot = new THREE.Group();
    pedestalRoot.name = "pedestal";
    const baseMat = makeSilverMaterial(PEDESTAL_SILVER, 0.72, 0x334155, 0.12);
    const rimMat = makeSilverMaterial(PEDESTAL_RIM, 0.85, 0x64748b, 0.18);
    const glowMat = new THREE.MeshStandardMaterial({
        color: PEDESTAL_GLOW,
        emissive: PEDESTAL_GLOW,
        emissiveIntensity: 0.55,
        roughness: 0.4,
        metalness: 0.2,
        flatShading: true,
        transparent: true,
        opacity: 0.75,
        depthWrite: false,
    });
    // Wide base plinth
    const baseGeo = new THREE.CylinderGeometry(1.85, 2.05, 0.28, 28);
    const base = new THREE.Mesh(baseGeo, baseMat);
    base.name = "pedestalBase";
    base.position.y = 0.14;
    base.castShadow = true;
    base.receiveShadow = true;
    pedestalRoot.add(base);
    // Mid silver disc
    const midGeo = new THREE.CylinderGeometry(1.55, 1.65, 0.22, 28);
    const mid = new THREE.Mesh(midGeo, rimMat);
    mid.name = "pedestalMid";
    mid.position.y = 0.35;
    mid.castShadow = true;
    pedestalRoot.add(mid);
    // Top deck (smooth silver circle)
    const deckGeo = new THREE.CylinderGeometry(1.35, 1.4, 0.12, 28);
    const deck = new THREE.Mesh(deckGeo, rimMat);
    deck.name = "pedestalDeck";
    deck.position.y = 0.5;
    deck.castShadow = true;
    pedestalRoot.add(deck);
    // Inner emissive ring (hologram projector)
    const ringGeo = new THREE.TorusGeometry(0.95, 0.06, 8, 32);
    const ring = new THREE.Mesh(ringGeo, glowMat);
    ring.name = "pedestalRing";
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.58;
    pedestalRoot.add(ring);
    // Center projector pad
    const padGeo = new THREE.CylinderGeometry(0.55, 0.6, 0.06, 16);
    const pad = new THREE.Mesh(padGeo, glowMat);
    pad.name = "pedestalPad";
    pad.position.y = 0.56;
    pedestalRoot.add(pad);
    // Ground halo so pedestal reads at distance through fog
    const haloGeo = new THREE.CircleGeometry(2.4, 28);
    const haloMat = new THREE.MeshBasicMaterial({
        color: PEDESTAL_GLOW,
        transparent: true,
        opacity: 0.22,
        depthWrite: false,
        side: THREE.DoubleSide,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.name = "pedestalHalo";
    halo.rotation.x = -Math.PI / 2;
    halo.position.y = 0.03;
    pedestalRoot.add(halo);
    group.add(pedestalRoot);
    // --- Hologram mecha (desire object, always spinning until solidify) ---
    const holoBodyMat = makeHoloMaterial(HOLO_BLUE, 0.42, 0.95);
    const holoCoreMat = makeHoloMaterial(HOLO_CORE, 0.55, 1.25);
    const holoEdgeMat = makeHoloMaterial(HOLO_EDGE, 0.5, 1.1);
    const hologramRoot = buildHologramMecha({
        body: holoBodyMat,
        core: holoCoreMat,
        edge: holoEdgeMat,
    });
    hologramRoot.position.y = HOLOGRAM_HOVER_Y;
    group.add(hologramRoot);
    // Soft vertical projection column (reads through fog)
    const columnGeo = new THREE.CylinderGeometry(0.9, 1.15, 5.2, 12, 1, true);
    const columnMat = new THREE.MeshBasicMaterial({
        color: HOLO_BLUE,
        transparent: true,
        opacity: 0.12,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
    });
    const column = new THREE.Mesh(columnGeo, columnMat);
    column.name = "hologramColumn";
    column.position.y = HOLOGRAM_HOVER_Y + 1.8;
    group.add(column);
    let spin = 0;
    let solidifyT = 1; // 1 = hologram, 0 = gone
    let absorbPulse = 0;
    let pedestalOnly = false;
    const applyHoloOpacity = () => {
        // Solidify: opacity climbs then drops as real mecha takes over
        // solidifyT 1 → pure holo, mid-high → denser, 0 → hidden
        const densify = solidifyT > 0.45
            ? 1 + (1 - solidifyT) * 1.4 // rising density first half
            : solidifyT / 0.45; // fade second half
        const baseOp = 0.38 * densify;
        const coreOp = 0.52 * densify;
        const edgeOp = 0.48 * densify;
        holoBodyMat.opacity = Math.min(0.92, baseOp);
        holoCoreMat.opacity = Math.min(0.95, coreOp);
        holoEdgeMat.opacity = Math.min(0.9, edgeOp);
        // Switch off additive when densifying (solidify into metal read)
        const additive = solidifyT > 0.55;
        const blend = additive ? THREE.AdditiveBlending : THREE.NormalBlending;
        holoBodyMat.blending = blend;
        holoCoreMat.blending = blend;
        holoEdgeMat.blending = blend;
        holoBodyMat.depthWrite = !additive && solidifyT > 0.2;
        holoBodyMat.emissiveIntensity = 0.95 + (1 - solidifyT) * 1.4;
        holoCoreMat.emissiveIntensity = 1.25 + (1 - solidifyT) * 1.8;
        columnMat.opacity = 0.08 + solidifyT * 0.1 + absorbPulse * 0.12;
        hologramRoot.visible = solidifyT > 0.02 && !pedestalOnly;
        column.visible = hologramRoot.visible;
    };
    const setPose = (x, z) => {
        group.position.set(x, 0, z);
    };
    const setVisible = (visible) => {
        group.visible = visible;
    };
    const setPedestalOnly = (only) => {
        pedestalOnly = only;
        applyHoloOpacity();
    };
    const setSolidify = (t) => {
        solidifyT = Math.max(0, Math.min(1, t));
        applyHoloOpacity();
    };
    const setAbsorbPulse = (intensity) => {
        absorbPulse = Math.max(0, Math.min(1, intensity));
        glowMat.emissiveIntensity = 0.55 + absorbPulse * 1.4;
        glowMat.opacity = 0.55 + absorbPulse * 0.4;
        haloMat.opacity = 0.18 + absorbPulse * 0.35;
        ring.scale.setScalar(1 + absorbPulse * 0.15);
    };
    const update = (dtSeconds, timeSeconds) => {
        if (!group.visible)
            return;
        const dt = Math.max(0, dtSeconds);
        spin += dt * HOLOGRAM_SPIN_RAD_S;
        hologramRoot.rotation.y = spin;
        // Subtle bob + approach shimmer
        const bob = Math.sin(timeSeconds * 1.7) * HOLOGRAM_BOB_AMP +
            Math.sin(timeSeconds * 3.1) * HOLOGRAM_BOB_AMP * 0.25;
        hologramRoot.position.y = HOLOGRAM_HOVER_Y + bob * solidifyT;
        // Slow scan pulse on core
        holoCoreMat.emissiveIntensity =
            1.15 + Math.sin(timeSeconds * 4.2) * 0.25 + absorbPulse * 0.8;
        // Halo breathe so far-away read stays alive
        const breathe = 0.18 + Math.sin(timeSeconds * 2.1) * 0.04 + absorbPulse * 0.3;
        haloMat.opacity = breathe;
    };
    const dispose = () => {
        group.traverse((obj) => {
            if (obj instanceof THREE.Mesh) {
                obj.geometry.dispose();
                const m = obj.material;
                if (Array.isArray(m))
                    m.forEach((x) => x.dispose());
                else
                    m.dispose();
            }
        });
        group.clear();
    };
    applyHoloOpacity();
    return {
        group,
        setPose,
        setVisible,
        setPedestalOnly,
        setSolidify,
        setAbsorbPulse,
        update,
        dispose,
    };
}
//# sourceMappingURL=pedestalHologram.js.map